import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import type { UserSummary } from '@oss/shared';
import { ApiError, api, tokenStore } from '@/lib/api-client';
import { AuthProvider } from '@/lib/permissions';

/**
 * Route guard.
 *
 * `/auth/me` is fetched rather than decoded from the token, because a role
 * change must take effect without waiting for the access token to expire —
 * and because a revoked session should bounce the user out on their next
 * navigation, not fifteen minutes later.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hasRefresh = Boolean(tokenStore.getRefresh());

  const { data, isLoading, isError } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get<UserSummary>('/auth/me'),
    enabled: hasRefresh,
    retry: (count, error) => {
      // A 401 here means the session is genuinely gone. Retrying would only
      // delay the redirect and burn the auth rate limit.
      if (error instanceof ApiError && error.status === 401) return false;
      return count < 1;
    },
    staleTime: 60_000,
  });

  if (!hasRefresh) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted" role="status">
        Signing in…
      </div>
    );
  }

  if (isError || !data) {
    tokenStore.clear();
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // An account that must enrol MFA gets exactly one destination until it does.
  if (data.mfaRequired && !data.mfaEnabled && location.pathname !== '/settings/mfa') {
    return <Navigate to="/settings/mfa" replace />;
  }

  return <AuthProvider user={data}>{children}</AuthProvider>;
}
